import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Search, Loader2, ChevronLeft, ChevronRight,
  Image as ImageIcon, Type, Check, AlertTriangle,
  Info, Plus, Trash2, RefreshCw, HelpCircle,
  Square, Layers
} from 'lucide-react';
import { adminConfigService } from '../../services/AdminConfigService';

// ============================================
// TOOLTIP COMPONENT
// ============================================
const Tooltip = ({ text, children }) => {
  const [show, setShow] = useState(false);
  
  return (
    <div className="relative inline-flex">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg max-w-xs whitespace-normal">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
};

// ============================================
// PRESETS DE TEXT CONSTRAINTS
// ============================================
const CONSTRAINT_PRESETS = [
  {
    id: 'title_bold',
    label: 'Título Principal',
    icon: '🔠',
    tooltip: 'Texto grande e bold. O agente de IA vai gerar frases curtas e impactantes para este layer.',
    values: { max_chars: 60, style: 'bold' }
  },
  {
    id: 'title_centered',
    label: 'Título Centralizado',
    icon: '📝',
    tooltip: 'Título centralizado com mais espaço. O agente vai criar textos mais elaborados, podendo ter múltiplas linhas.',
    values: { max_chars: 80, style: 'bold centered' }
  },
  {
    id: 'subtitle_upper',
    label: 'Subtítulo UPPERCASE',
    icon: '🏷️',
    tooltip: 'Texto curto em MAIÚSCULAS. O agente vai gerar labels curtos como "OFERTA ESPECIAL", "NOVO", etc.',
    values: { max_chars: 30, style: 'UPPERCASE' }
  },
  {
    id: 'tagline',
    label: 'Tagline / Tag',
    icon: '✨',
    tooltip: 'Badge ou etiqueta curta. Ideal para categorias ou destaques rápidos no post.',
    values: { max_chars: 25, style: 'UPPERCASE' }
  },
  {
    id: 'description',
    label: 'Descrição / Detalhes',
    icon: '📄',
    tooltip: 'Texto descritivo mais longo. O agente vai gerar frases informativas sobre o produto/serviço.',
    values: { max_chars: 80, style: 'normal' }
  },
  {
    id: 'url_cta',
    label: 'URL / CTA',
    icon: '🔗',
    tooltip: 'Link, site ou call-to-action. O agente vai inserir o site da marca ou texto como "Saiba mais".',
    values: { max_chars: 40, style: 'normal' }
  },
  {
    id: 'name_bold',
    label: 'Nome / Pessoa',
    icon: '👤',
    tooltip: 'Nome de pessoa ou marca em destaque. Texto bold, ideal para nomes próprios.',
    values: { max_chars: 30, style: 'UPPERCASE bold' }
  },
  {
    id: 'position',
    label: 'Cargo / Posição',
    icon: '💼',
    tooltip: 'Cargo ou descrição curta abaixo de um nome. Texto normal, sem destaque.',
    values: { max_chars: 50, style: 'normal' }
  },
  {
    id: 'price',
    label: 'Preço / Valor',
    icon: '💰',
    tooltip: 'Texto de preço grande. O agente vai formatar valores como "R$ 99,90" ou "50% OFF".',
    values: { max_chars: 15, style: 'large' }
  },
  {
    id: 'date',
    label: 'Data / Evento',
    icon: '📅',
    tooltip: 'Data ou período curto. O agente vai gerar textos como "22 JULHO" ou "BLACK FRIDAY".',
    values: { max_chars: 15, style: 'UPPERCASE' }
  },
  {
    id: 'custom',
    label: 'Personalizado',
    icon: '⚙️',
    tooltip: 'Defina manualmente o limite de caracteres e o estilo. Controle total sobre como o agente trata este layer.',
    values: { max_chars: 60, style: 'normal' }
  }
];

// ============================================
// MAIN MODAL COMPONENT
// ============================================
const PlacidTemplateImportModal = ({ isOpen, onClose, onImported }) => {
  // Steps: 'browse' | 'configure' | 'importing'
  const [step, setStep] = useState('browse');
  
  // Browse state
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ prev: null, next: null });
  
  // Selected template state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  // Configure state
  const [templateId, setTemplateId] = useState('');
  const [description, setDescription] = useState('');
  const [textConstraints, setTextConstraints] = useState({});
  const [selectedPresets, setSelectedPresets] = useState({});
  const [enabled, setEnabled] = useState(true);
  
  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(null);

  // ============================================
  // LOAD AVAILABLE TEMPLATES
  // ============================================
  const loadTemplates = useCallback(async (pageUrl = null) => {
    setIsLoadingList(true);
    try {
      const response = await adminConfigService.getAvailablePlacidTemplates({
        page: pageUrl,
        titleFilter: searchTerm || undefined
      });
      
      setAvailableTemplates(response.data || []);
      setPagination(response.pagination || { prev: null, next: null });
    } catch (err) {
      console.error('Erro ao carregar templates:', err);
    } finally {
      setIsLoadingList(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (isOpen && step === 'browse') {
      loadTemplates();
    }
  }, [isOpen, step, loadTemplates]);

  // Debounce search
  useEffect(() => {
    if (!isOpen) return;
    const timeout = setTimeout(() => {
      loadTemplates();
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // ============================================
  // SELECT TEMPLATE & LOAD DETAILS
  // ============================================
  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    setIsLoadingDetails(true);
    setImportError(null);
    
    try {
      const response = await adminConfigService.getAvailablePlacidTemplateDetails(template.uuid);
      const details = response.data;
      
      setTemplateDetails(details);
      setTemplateId(details.suggested_id || '');
      setDescription(details.suggested_description || '');
      setTextConstraints(details.suggested_constraints || {});
      setEnabled(true);
      
      // Pre-selecionar presets baseado nas sugestões
      const presets = {};
      Object.entries(details.suggested_constraints || {}).forEach(([layerName, constraint]) => {
        const matchingPreset = CONSTRAINT_PRESETS.find(p => 
          p.values.style === constraint.style && p.values.max_chars === constraint.max_chars
        );
        presets[layerName] = matchingPreset?.id || 'custom';
      });
      setSelectedPresets(presets);
      
      setStep('configure');
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      setImportError('Erro ao carregar detalhes do template. Tente novamente.');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // ============================================
  // CONSTRAINT MANAGEMENT
  // ============================================
  const handlePresetChange = (layerName, presetId) => {
    const preset = CONSTRAINT_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    
    setSelectedPresets(prev => ({ ...prev, [layerName]: presetId }));
    
    if (presetId !== 'custom') {
      setTextConstraints(prev => ({
        ...prev,
        [layerName]: { ...preset.values }
      }));
    }
  };

  const handleConstraintChange = (layerName, field, value) => {
    setTextConstraints(prev => ({
      ...prev,
      [layerName]: {
        ...prev[layerName],
        [field]: field === 'max_chars' ? parseInt(value) || 0 : value
      }
    }));
    setSelectedPresets(prev => ({ ...prev, [layerName]: 'custom' }));
  };

  // ============================================
  // IMPORT
  // ============================================
  const handleImport = async () => {
    if (!templateDetails || !templateId.trim()) return;
    
    setIsImporting(true);
    setImportError(null);
    
    try {
      const importData = {
        template_id: templateId.trim(),
        uuid: templateDetails.uuid,
        title: templateDetails.title,
        description: description,
        layers: [
          ...(templateDetails.picture_layers || []),
          ...(templateDetails.text_layers || []),
          ...(templateDetails.other_layers || [])
        ],
        picture_layers: templateDetails.picture_layers || [],
        text_layers: templateDetails.text_layers || [],
        other_layers: templateDetails.other_layers?.length > 0 ? templateDetails.other_layers : undefined,
        text_constraints: textConstraints,
        enabled: enabled
      };
      
      await adminConfigService.importPlacidTemplate(importData);
      
      if (onImported) onImported();
      handleClose();
      
    } catch (err) {
      console.error('Erro ao importar:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Erro ao importar template';
      setImportError(errorMsg);
    } finally {
      setIsImporting(false);
    }
  };

  // ============================================
  // CLOSE / RESET
  // ============================================
  const handleClose = () => {
    setStep('browse');
    setSelectedTemplate(null);
    setTemplateDetails(null);
    setSearchTerm('');
    setImportError(null);
    onClose();
  };

  const handleBack = () => {
    setStep('browse');
    setSelectedTemplate(null);
    setTemplateDetails(null);
    setImportError(null);
  };

  if (!isOpen) return null;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            {step === 'configure' && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {step === 'browse' ? 'Importar Template do Placid' : 'Configurar Template'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {step === 'browse'
                  ? 'Selecione um template da sua conta Placid'
                  : `Configurando: ${selectedTemplate?.title}`
                }
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error */}
        {importError && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{importError}</span>
            <button onClick={() => setImportError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* ==========================================
              STEP: BROWSE
              ========================================== */}
          {step === 'browse' && (
            <div>
              {/* Search */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar templates por título..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>

              {/* Loading */}
              {isLoadingList && (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                </div>
              )}

              {/* Template Grid */}
              {!isLoadingList && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {availableTemplates.map(template => (
                      <button
                        key={template.uuid}
                        onClick={() => !template.already_imported && handleSelectTemplate(template)}
                        disabled={template.already_imported || isLoadingDetails}
                        className={`relative text-left rounded-xl border-2 overflow-hidden transition-all ${
                          template.already_imported
                            ? 'border-gray-200 opacity-60 cursor-not-allowed'
                            : 'border-gray-200 hover:border-amber-400 hover:shadow-lg cursor-pointer'
                        }`}
                      >
                        {/* Thumbnail */}
                        <div className="aspect-square bg-gray-100 relative">
                          {template.thumbnail ? (
                            <img
                              src={template.thumbnail}
                              alt={template.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Layers className="w-12 h-12 text-gray-300" />
                            </div>
                          )}
                          
                          {/* Already imported badge */}
                          {template.already_imported && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <span className="bg-white text-gray-700 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5">
                                <Check className="w-3.5 h-3.5" />
                                Já importado
                              </span>
                            </div>
                          )}
                          
                          {/* Loading overlay for selected */}
                          {isLoadingDetails && selectedTemplate?.uuid === template.uuid && (
                            <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                              <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="p-3">
                          <h3 className="font-semibold text-sm text-gray-900 truncate">
                            {template.title}
                          </h3>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-gray-500">
                              {template.layers?.length || 0} layers
                            </span>
                            {template.tags?.length > 0 && (
                              <span className="text-xs text-gray-400">
                                • {template.tags.slice(0, 2).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Empty state */}
                  {availableTemplates.length === 0 && !isLoadingList && (
                    <div className="text-center py-16">
                      <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">Nenhum template encontrado</p>
                    </div>
                  )}

                  {/* Pagination */}
                  {(pagination.prev || pagination.next) && (
                    <div className="flex items-center justify-center gap-3 mt-6">
                      <button
                        onClick={() => pagination.prev && loadTemplates(pagination.prev)}
                        disabled={!pagination.prev}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Anterior
                      </button>
                      <button
                        onClick={() => pagination.next && loadTemplates(pagination.next)}
                        disabled={!pagination.next}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Próximo
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ==========================================
              STEP: CONFIGURE
              ========================================== */}
          {step === 'configure' && templateDetails && (
            <div className="space-y-6">
              
              {/* Template Preview */}
              <div className="flex gap-4 p-4 bg-gray-50 rounded-xl">
                {selectedTemplate?.thumbnail && (
                  <img
                    src={selectedTemplate.thumbnail}
                    alt={selectedTemplate.title}
                    className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900">{templateDetails.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">UUID: {templateDetails.uuid}</p>
                  <div className="flex gap-3 mt-2">
                    <span className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                      <ImageIcon className="w-3 h-3" />
                      {templateDetails.picture_layers?.length || 0} imagens
                    </span>
                    <span className="flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full">
                      <Type className="w-3 h-3" />
                      {templateDetails.text_layers?.length || 0} textos
                    </span>
                    {templateDetails.other_layers?.length > 0 && (
                      <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        <Square className="w-3 h-3" />
                        {templateDetails.other_layers.length} outros
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Warnings */}
              {templateDetails.uuid_exists && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  Este template (UUID) já está importado. Importar novamente vai sobrescrever a configuração existente.
                </div>
              )}

              {/* Template ID */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm font-semibold text-gray-700">ID do Template</label>
                  <Tooltip text="Identificador único usado internamente. Gerado automaticamente a partir do título. Não precisa alterar na maioria dos casos.">
                    <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                  </Tooltip>
                </div>
                <input
                  type="text"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 font-mono text-sm"
                  placeholder="ex: fullscreen_com_texto"
                />
                {templateDetails.id_exists && (
                  <p className="text-xs text-amber-600 mt-1">⚠️ Este ID já existe e será sobrescrito</p>
                )}
              </div>

              {/* Description */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm font-semibold text-gray-700">Descrição do Template</label>
                  <Tooltip text="A descrição é enviada ao agente de IA como contexto. Quanto mais detalhada, melhor o agente entende onde colocar cada texto e como deve ser o estilo visual. Inclua informações sobre cores, posição dos elementos e propósito de cada layer.">
                    <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                  </Tooltip>
                </div>
                <textarea
                  rows={8}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 text-sm font-mono"
                  placeholder="Descreva o layout, posição dos elementos, cores padrão..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Dica: Descreva a posição e função de cada layer para o agente de IA gerar conteúdos adequados.
                </p>
              </div>

              {/* Layers de Imagem */}
              {templateDetails.picture_layers?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <ImageIcon className="w-4 h-4 text-blue-600" />
                    <label className="text-sm font-semibold text-gray-700">Layers de Imagem</label>
                    <Tooltip text="Esses layers receberão imagens geradas por IA ou fornecidas pelo usuário. O agente decide automaticamente qual imagem usar para cada layer.">
                      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                    </Tooltip>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {templateDetails.picture_layers.map(layer => (
                      <span key={layer} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
                        🖼️ {layer}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Text Constraints */}
              {templateDetails.text_layers?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Type className="w-4 h-4 text-purple-600" />
                    <label className="text-sm font-semibold text-gray-700">Configuração dos Layers de Texto</label>
                    <Tooltip text="Defina como o agente de IA deve tratar cada layer de texto. O preset controla o limite de caracteres e o estilo, influenciando diretamente o conteúdo que o agente gera.">
                      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                    </Tooltip>
                  </div>
                  
                  <div className="space-y-4">
                    {templateDetails.text_layers.map(layerName => (
                      <div key={layerName} className="border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="font-semibold text-gray-900">{layerName}</span>
                        </div>
                        
                        {/* Preset Selector */}
                        <div className="mb-3">
                          <label className="text-xs font-medium text-gray-500 mb-1.5 block">Tipo do Layer</label>
                          <div className="flex flex-wrap gap-1.5">
                            {CONSTRAINT_PRESETS.map(preset => (
                              <Tooltip key={preset.id} text={preset.tooltip}>
                                <button
                                  onClick={() => handlePresetChange(layerName, preset.id)}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                    selectedPresets[layerName] === preset.id
                                      ? 'bg-amber-500 text-white shadow-sm'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  <span>{preset.icon}</span>
                                  {preset.label}
                                </button>
                              </Tooltip>
                            ))}
                          </div>
                        </div>
                        
                        {/* Manual Controls (always visible) */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="flex items-center gap-1.5 mb-1">
                              <label className="text-xs font-medium text-gray-500">Máx. Caracteres</label>
                              <Tooltip text="Limite de caracteres que o agente de IA deve respeitar ao gerar o texto para este layer. Valores menores = textos mais curtos e impactantes.">
                                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                              </Tooltip>
                            </div>
                            <input
                              type="number"
                              min="5"
                              max="200"
                              value={textConstraints[layerName]?.max_chars || 60}
                              onChange={(e) => handleConstraintChange(layerName, 'max_chars', e.target.value)}
                              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 mb-1">
                              <label className="text-xs font-medium text-gray-500">Estilo</label>
                              <Tooltip text="Estilo visual do texto. 'UPPERCASE' = letras maiúsculas, 'bold' = negrito, 'centered' = centralizado. Esses estilos orientam o agente de IA sobre o tom do texto.">
                                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                              </Tooltip>
                            </div>
                            <input
                              type="text"
                              value={textConstraints[layerName]?.style || 'normal'}
                              onChange={(e) => handleConstraintChange(layerName, 'style', e.target.value)}
                              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                              placeholder="ex: bold, UPPERCASE, normal"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Enabled toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-semibold text-gray-900">Ativar imediatamente</p>
                  <p className="text-xs text-gray-500 mt-0.5">Se ativado, o template será usado nas próximas gerações</p>
                </div>
                <button
                  onClick={() => setEnabled(!enabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    enabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'configure' && (
          <div className="flex items-center justify-between p-6 border-t border-gray-100 flex-shrink-0 bg-gray-50">
            <button
              onClick={handleBack}
              className="px-5 py-2.5 text-gray-600 hover:bg-gray-200 rounded-xl font-medium transition-colors"
            >
              Voltar
            </button>
            <button
              onClick={handleImport}
              disabled={isImporting || !templateId.trim() || !description.trim()}
              className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isImporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Importar Template
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlacidTemplateImportModal;